import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Building2, Plus, Users, Receipt, Settings, ChevronRight, Image, Upload, Trash2, Wallet } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GestioneAziende = () => {
  const { user, isSindacatoAdmin, hasPermesso } = useAuth();
  const navigate = useNavigate();
  const [aziende, setAziende] = useState([]);
  const [ruoliAzienda, setRuoliAzienda] = useState([]);
  const [saldiAziende, setSaldiAziende] = useState({});
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'AZIENDA',
    descrizione: ''
  });
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef({});

  const isAdmin = isSindacatoAdmin();
  
  // Verifica se l'utente è membro dello Staff
  const isStaffMember = () => {
    if (isAdmin) return true;
    return user?.impieghi?.some(imp => imp.azienda_nome?.toLowerCase().includes('staff')) ||
           user?.azienda_nome?.toLowerCase().includes('staff');
  };

  // Ottieni il livello gerarchico dell'utente in un'azienda
  const getUserLevelInAzienda = (aziendaId) => {
    const impiego = user?.impieghi?.find(imp => imp.azienda_id === aziendaId);
    if (!impiego) return 0;
    // Cerca il ruolo nei ruoliAzienda caricati
    const ruolo = ruoliAzienda.find(r => r.id === impiego.ruolo_azienda_id);
    return ruolo?.livello_gerarchia || 0;
  };

  // Verifica se l'utente può vedere il gestionale di un'azienda (livello 9 o 10)
  const canAccessGestionale = (aziendaId) => {
    if (isAdmin) return true;
    const level = getUserLevelInAzienda(aziendaId);
    return level >= 9;
  };

  useEffect(() => {
    fetchAziende();
  }, []);

  const fetchAziende = async () => {
    try {
      // Carica prima tutti i ruoli azienda per conoscere i livelli
      const ruoliRes = await axios.get(`${API_URL}/ruoli-azienda`).catch(() => ({ data: [] }));
      setRuoliAzienda(ruoliRes.data);
      
      const res = await axios.get(`${API_URL}/aziende`);
      let aziendeData = res.data;
      
      // Se non è admin, filtra solo le aziende dove l'utente ha livello >= 9
      if (!isAdmin) {
        const userAziendeIds = user?.impieghi?.map(imp => imp.azienda_id) || [];
        if (user?.azienda_id && !userAziendeIds.includes(user.azienda_id)) {
          userAziendeIds.push(user.azienda_id);
        }
        
        // Filtra solo le aziende dove ha livello 9-10
        aziendeData = aziendeData.filter(az => {
          if (!userAziendeIds.includes(az.id)) return false;
          const impiego = user?.impieghi?.find(imp => imp.azienda_id === az.id);
          if (!impiego) return false;
          const ruolo = ruoliRes.data.find(r => r.id === impiego.ruolo_azienda_id);
          return (ruolo?.livello_gerarchia || 0) >= 9;
        });
      }
      
      // Carica info aggiuntive per ogni azienda
      const aziendeConInfo = await Promise.all(aziendeData.map(async (az) => {
        try {
          const [dipendentiRes, scontriniRes, contoRes] = await Promise.all([
            axios.get(`${API_URL}/users`).catch(() => ({ data: [] })),
            axios.get(`${API_URL}/scontrini?azienda_id=${az.id}`).catch(() => ({ data: [] })),
            axios.get(`${API_URL}/banca/conto-azienda/${az.id}`).catch(() => ({ data: { saldo: 0 } }))
          ]);
          
          const dipendenti = dipendentiRes.data.filter(u => 
            u.azienda_id === az.id || u.impieghi?.some(imp => imp.azienda_id === az.id)
          );
          
          return {
            ...az,
            dipendenti_count: dipendenti.length,
            scontrini_count: scontriniRes.data.length,
            saldo_conto: contoRes.data?.saldo || 0
          };
        } catch {
          return { ...az, dipendenti_count: 0, scontrini_count: 0, saldo_conto: 0 };
        }
      }));
      
      setAziende(aziendeConInfo);
    } catch (error) {
      toast.error('Errore nel caricamento delle aziende');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/aziende`, formData);
      toast.success('Azienda creata');
      setCreateDialogOpen(false);
      setFormData({ nome: '', tipo: 'AZIENDA', descrizione: '' });
      fetchAziende();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const enterAzienda = (azienda) => {
    // Naviga alla pagina dell'azienda specifica
    navigate(`/azienda/${azienda.id}`);
  };

  const handleImageUpload = async (e, aziendaId) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingId(aziendaId);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API_URL}/aziende/${aziendaId}/immagine`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Immagine caricata');
      fetchAziende();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteImage = async (e, aziendaId) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare l\'immagine?')) return;
    
    try {
      await axios.delete(`${API_URL}/aziende/${aziendaId}/immagine`);
      toast.success('Immagine eliminata');
      fetchAziende();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getTipoBadge = (tipo) => {
    const colors = {
      'AZIENDA': 'bg-blue-100 text-blue-800',
      'ENTE_PUBBLICO': 'bg-green-100 text-green-800',
      'FORZE_ORDINE': 'bg-red-100 text-red-800',
      'SANITA': 'bg-pink-100 text-pink-800',
      'ISTRUZIONE': 'bg-purple-100 text-purple-800',
      'BANCA': 'bg-amber-100 text-amber-800'
    };
    return <Badge className={colors[tipo] || 'bg-slate-100 text-slate-800'}>{tipo?.replace('_', ' ')}</Badge>;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="gestione-aziende-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-header flex items-center gap-3">
            <Building2 className="w-8 h-8" />
            Gestione Aziende
          </h1>
          <p className="text-slate-600 mt-1">
            {isAdmin 
              ? 'Gestisci tutte le aziende del sistema' 
              : 'Le tue aziende'}
          </p>
        </div>
        {isAdmin && (
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Azienda
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crea Nuova Azienda</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Nome Azienda</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Nome dell'azienda"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select
                    value={formData.tipo}
                    onValueChange={(v) => setFormData({ ...formData, tipo: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AZIENDA">Azienda</SelectItem>
                      <SelectItem value="ENTE_PUBBLICO">Ente Pubblico</SelectItem>
                      <SelectItem value="FORZE_ORDINE">Forze dell'Ordine</SelectItem>
                      <SelectItem value="SANITA">Sanità</SelectItem>
                      <SelectItem value="ISTRUZIONE">Istruzione</SelectItem>
                      <SelectItem value="BANCA">Banca</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Input
                    value={formData.descrizione}
                    onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                    placeholder="Descrizione (opzionale)"
                  />
                </div>
                
                {/* Avviso conto banca automatico */}
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <Wallet className="w-5 h-5" />
                    <span className="text-sm font-medium">Conto Bancario</span>
                  </div>
                  <p className="text-xs text-emerald-600 mt-1">
                    Verrà creato automaticamente un conto bancario aziendale con saldo €0,00
                  </p>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                    Crea Azienda
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Lista Aziende */}
      {aziende.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Building2 className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600">
              {isAdmin ? 'Nessuna azienda presente' : 'Non fai parte di nessuna azienda'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {aziende.map((azienda) => (
            <Card 
              key={azienda.id} 
              className="hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => enterAzienda(azienda)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      {azienda.immagine_url ? (
                        <>
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${azienda.immagine_url}`} 
                            alt={azienda.nome}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                          {isAdmin && (
                            <button
                              onClick={(e) => handleDeleteImage(e, azienda.id)}
                              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Rimuovi immagine"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </>
                      ) : (
                        <div 
                          className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center relative"
                          onClick={(e) => {
                            if (isAdmin) {
                              e.stopPropagation();
                              fileInputRef.current[azienda.id]?.click();
                            }
                          }}
                        >
                          {uploadingId === azienda.id ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600"></div>
                          ) : (
                            <>
                              <Building2 className="w-6 h-6 text-slate-400" />
                              {isAdmin && (
                                <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Upload className="w-4 h-4 text-white" />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      {isAdmin && (
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          ref={el => fileInputRef.current[azienda.id] = el}
                          onChange={(e) => handleImageUpload(e, azienda.id)}
                        />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{azienda.nome}</CardTitle>
                      {getTipoBadge(azienda.tipo)}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600 transition-colors" />
                </div>
              </CardHeader>
              <CardContent>
                {azienda.descrizione && (
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{azienda.descrizione}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {azienda.dipendenti_count || 0} dipendenti
                  </span>
                  <span className="flex items-center gap-1">
                    <Wallet className="w-4 h-4 text-emerald-500" />
                    <span className={azienda.saldo_conto >= 0 ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
                      {formatCurrency(azienda.saldo_conto)}
                    </span>
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default GestioneAziende;
